import type { User } from '@/types'

interface PermissionAccess {
  can_view?: boolean
  can_create?: boolean
  can_edit?: boolean
  can_delete?: boolean
  can_export?: boolean
  can_import?: boolean
}

interface RouteInfo {
  name: string
  icon: string
  parentId: number | null
  childId: number
}

interface AccessResult {
  can_view: boolean
  can_create: boolean
  can_edit: boolean
  can_delete: boolean
  can_export: boolean
  can_import: boolean
  route: RouteInfo | null
}

interface UserRoute {
  id?: number
  path: string
  name: string
  icon?: string
  side_nav?: string
  children?: UserRoute[]
  is_parent_route?: boolean // Flag to indicate this is a parent route (create/edit) generated for parent navigation
}

interface UserRole {
  permissions?: Record<number | string, Record<number | string, PermissionAccess>> | string
}

export class AccessHelper {
  private user: User | null
  private userRoutes: UserRoute[]
  private permissions: Record<string, Record<string, PermissionAccess>> = {}

  constructor(user: User | null, userRoutes: UserRoute[] = []) {
    this.user = user
    this.userRoutes = userRoutes

    if (!user) {
      return
    }

    let userRoleObj: UserRole | null = null
    const userRole = (user as any)?.user_role

    if (userRole) {
      if (typeof userRole === 'string') {
        try {
          userRoleObj = JSON.parse(userRole)
        } catch (error) {
          console.error('[AccessHelper] Failed to parse user_role:', error)
          userRoleObj = null
        }
      } else {
        userRoleObj = userRole as UserRole
      }

      this.permissions = this.parsePermissions(userRoleObj?.permissions)
    }
  }

  private parsePermissions(
    permissionsStr: Record<number | string, Record<number | string, PermissionAccess>> | string | undefined
  ): Record<string, Record<string, PermissionAccess>> {
    try {
      if (!permissionsStr) {
        return {}
      }

      let parsed: any
      if (typeof permissionsStr === 'string') {
        parsed = JSON.parse(permissionsStr)
      } else {
        parsed = permissionsStr
      }

      const normalized: Record<string, Record<string, PermissionAccess>> = {}

      Object.keys(parsed).forEach((parentKey) => {
        const parentPermissions = parsed[parentKey]
        if (typeof parentPermissions === 'object' && parentPermissions !== null) {
          normalized[String(parentKey)] = {}
          Object.keys(parentPermissions).forEach((childKey) => {
            normalized[String(parentKey)][String(childKey)] = parentPermissions[childKey]
          })
        }
      })

      return normalized
    } catch (error) {
      console.error('[AccessHelper] Failed to parse permissions:', error)
      return {}
    }
  }

  private matchPath(routePath: string, currentPath: string): boolean {
    const routeSegments = routePath.split('/').filter(Boolean)
    const currentSegments = currentPath.split('/').filter(Boolean)

    if (routeSegments.length > currentSegments.length) {
      return false
    }

    const routePathNormalized = '/' + routeSegments.join('/')
    const currentPathNormalized = '/' + currentSegments.join('/')

    if (
      currentPathNormalized.startsWith(routePathNormalized + '/') ||
      currentPathNormalized === routePathNormalized
    ) {
      return true
    }

    if (routeSegments.length === currentSegments.length) {
      return routeSegments.every((segment, i) => {
        return segment.startsWith(':') || segment === currentSegments[i]
      })
    }

    return false
  }

  private findRouteWithParent(
    items: UserRoute[],
    targetPath: string,
    parentId: number | null = null
  ): { parentId: number | null; childId: number; name: string; icon: string } | null {
    const normalizedTarget = targetPath.replace(/\/$/, '') || '/'

    for (const item of items) {
      if (!item.path) continue

      const normalizedItemPath = item.path.replace(/\/$/, '') || '/'

      if (item.children && item.children.length > 0) {
        for (const child of item.children) {
          if (!child.path) continue

          if (this.matchPath(child.path, normalizedTarget)) {
            // If this is a parent route (create/edit route generated for parent), use parent's ID for permission lookup
            const isParentRoute = (child as any).is_parent_route === true
            const parentIdValue = item.id || parentId || 0
            const childIdForPermissions = isParentRoute ? parentIdValue : (child.id || 0)
            
            return {
              parentId: item.id || parentId,
              childId: childIdForPermissions,
              name: child.name,
              icon: child.icon || '',
            }
          }

          const childPathNormalized = child.path.replace(/\/$/, '') || '/'
          const combinedPath =
            normalizedItemPath === '/' ? childPathNormalized : normalizedItemPath + childPathNormalized

          if (normalizedTarget === combinedPath || this.matchPath(combinedPath, normalizedTarget)) {
            // If this is a parent route (create/edit route generated for parent), use parent's ID for permission lookup
            const isParentRoute = (child as any).is_parent_route === true
            const parentIdValue = item.id || parentId || 0
            const childIdForPermissions = isParentRoute ? parentIdValue : (child.id || 0)
            
            return {
              parentId: item.id || parentId,
              childId: childIdForPermissions,
              name: child.name,
              icon: child.icon || '',
            }
          }

          if (
            normalizedTarget.startsWith(normalizedItemPath) &&
            normalizedTarget.endsWith(childPathNormalized)
          ) {
            // If this is a parent route (create/edit route generated for parent), use parent's ID for permission lookup
            const isParentRoute = (child as any).is_parent_route === true
            const parentIdValue = item.id || parentId || 0
            const childIdForPermissions = isParentRoute ? parentIdValue : (child.id || 0)
            
            return {
              parentId: item.id || parentId,
              childId: childIdForPermissions,
              name: child.name,
              icon: child.icon || '',
            }
          }
        }

        const found = this.findRouteWithParent(item.children, normalizedTarget, item.id || parentId)
        if (found) {
          return found
        }
      }

      if (this.matchPath(normalizedItemPath, normalizedTarget)) {
        const itemSegments = normalizedItemPath.split('/').filter(Boolean)
        const targetSegments = normalizedTarget.split('/').filter(Boolean)

        if (itemSegments.length === targetSegments.length) {
          return {
            parentId,
            childId: item.id || 0,
            name: item.name,
            icon: item.icon || '',
          }
        }
      }
    }
    return null
  }

  hasAccess(pathname?: string): AccessResult {
    const currentPath = pathname || (typeof window !== 'undefined' ? window.location.pathname : '/')
    const route = this.findRouteWithParent(this.userRoutes, currentPath)

    if (!route) {
      return {
        can_view: false,
        can_create: false,
        can_edit: false,
        can_delete: false,
        can_export: false,
        can_import: false,
        route: null,
      }
    }

    const { parentId, childId, name, icon } = route
    const parentKey = parentId !== null ? String(parentId) : '0'
    const childKey = String(childId)
    const access = this.permissions[parentKey]?.[childKey]

    return {
      can_view: !!access?.can_view,
      can_create: !!access?.can_create,
      can_edit: !!access?.can_edit,
      can_delete: !!access?.can_delete,
      can_export: !!access?.can_export,
      can_import: !!access?.can_import,
      route: {
        name,
        icon,
        parentId,
        childId,
      },
    }
  }

  hasPermission(permission: keyof PermissionAccess, pathname?: string): boolean {
    const access = this.hasAccess(pathname)
    return access[permission] || false
  }
}

export default AccessHelper


